import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, ExternalLink, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface JourneyExternalRegistrationStepProps {
  stepId: string;
  userId: string;
  registrationUrl: string;
  buttonText: string;
  confirmationText: string;
  introContent?: string;
  ontraportTagOnComplete?: string;
  isCompleted: boolean;
  onComplete: () => void;
}

export function JourneyExternalRegistrationStep({
  stepId,
  userId,
  registrationUrl,
  buttonText,
  confirmationText,
  introContent,
  ontraportTagOnComplete,
  isCompleted,
  onComplete,
}: JourneyExternalRegistrationStepProps) {
  const [hasRegistered, setHasRegistered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleOpenRegistration = () => {
    if (registrationUrl) {
      window.open(registrationUrl, '_blank');
    } else {
      toast({
        title: "Registration link not available",
        description: "The registration link will be added soon.",
        variant: "destructive",
      });
    }
  };

  const handleConfirm = async () => {
    if (!hasRegistered) return;

    setIsSubmitting(true);
    try {
      // Mark step as completed
      const { error } = await supabase
        .from('user_journey_step_progress')
        .upsert({
          user_id: userId,
          step_id: stepId,
          status: 'completed',
          completed_at: new Date().toISOString(),
          verified_completion: true,
          action_data: { registered: true },
        }, { onConflict: 'user_id,step_id' });

      if (error) throw error;

      // Add OntraPort tag if configured
      if (ontraportTagOnComplete) {
        try {
          console.log('Adding OntraPort tag:', ontraportTagOnComplete);
          await supabase.functions.invoke('add-ontraport-tag', {
            body: { 
              userId, 
              tagName: ontraportTagOnComplete 
            }
          });
        } catch (tagError) {
          console.error('Failed to add OntraPort tag:', tagError);
          // Don't fail the step completion for tag errors
        }
      }

      // Unlock next step
      const { data: currentStep } = await supabase
        .from('journey_steps')
        .select('step_number')
        .eq('id', stepId)
        .single();

      if (currentStep) {
        const { data: nextStep } = await supabase
          .from('journey_steps')
          .select('id')
          .eq('step_number', currentStep.step_number + 1)
          .eq('is_active', true)
          .single();

        if (nextStep) {
          await supabase
            .from('user_journey_step_progress')
            .upsert({
              user_id: userId,
              step_id: nextStep.id,
              status: 'active',
              started_at: new Date().toISOString(),
            }, { onConflict: 'user_id,step_id' });
        }
      }

      onComplete();
    } catch (error) {
      console.error('Error completing step:', error);
      toast({
        title: "Error",
        description: "Failed to save progress. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isCompleted) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Registration confirmed</span>
        </div>
        
        {introContent && (
          <div className="bg-muted/30 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">{introContent}</p>
          </div>
        )}
        
        {confirmationText && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span>{confirmationText}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Intro Content */}
      {introContent && (
        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {introContent}
          </p>
        </div>
      )}

      {/* Registration Button */}
      <div className="flex flex-col items-center gap-4 p-6 bg-muted/30 rounded-lg">
        <p className="text-center text-muted-foreground">
          Click the button below to open the registration page in a new tab.
        </p>
        <Button
          onClick={handleOpenRegistration}
          size="lg"
          className="gap-2"
        >
          <ExternalLink className="w-4 h-4" />
          {buttonText || "Register"}
        </Button>
      </div>

      {/* Confirmation Checkbox */}
      <div className="flex items-start gap-3 p-4 border rounded-lg">
        <Checkbox
          id={`confirm-${stepId}`}
          checked={hasRegistered}
          onCheckedChange={(checked) => setHasRegistered(checked === true)}
          className="mt-0.5"
        />
        <label
          htmlFor={`confirm-${stepId}`}
          className="text-sm font-medium cursor-pointer leading-relaxed"
        >
          {confirmationText || "I have completed the registration"}
        </label>
      </div>

      {/* Continue Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleConfirm}
          disabled={!hasRegistered || isSubmitting}
          size="lg"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            "Acknowledge and Continue"
          )}
        </Button>
      </div>
    </div>
  );
}
